import React from "react";
import { Helmet } from "react-helmet-async";
import { DogIcon as Dog, ShieldCheckIcon as ShieldCheck, TreeStructureIcon as TreeStructure, HeartStraightIcon as HeartStraight, CheckCircleIcon as CheckCircle, StorefrontIcon as Storefront } from "@phosphor-icons/react";
import ReelStage, {
  Beat,
  beatProgress,
  easeOutCubic,
  easeInOutCubic,
  useTimeline,
} from "./ReelStage";
import { WaxSeal } from "@/components/luxury";

/**
 * Buyer-side marketing reel — 15s, 1080×1920 (or 1920×1080 with `?o=horizontal`).
 *
 * Story arc:
 *   0.0–2.8 — Hook: ink + gold stamp + the buyer headline.
 *   2.8–5.6 — Marketplace card grid parallax-scrolls upward.
 *   5.6–8.4 — Single listing zooms forward, verified WaxSeal pulses.
 *   8.4–11.2 — Pedigree fan: 6-gen lineage opens like an heraldic crest.
 *   11.2–13.4 — Buyer Pass card slides in: escrow, health, refundable.
 *   13.4–15.0 — Closing WaxSeal + wordmark (rendered by ReelStage).
 *
 * Aesthetic discipline: ink ground, cream paper accents, gold hairlines,
 * crimson sparingly for "verified". Typography is Spectral display +
 * Manrope tabular numerals. No emoji, no vector art outside Phosphor.
 *
 * Recording: open at exactly 1080×1920 viewport (or use a screen-recorder
 * crop), append `?clean=1` to hide the dimension marker, capture one full
 * 15s loop. The reel auto-loops with a 0.6s ink hold for clean cuts.
 */
export default function MarketingReelBuyer() {
  return (
    <>
      <Helmet>
        <title>Reel — Buyer · Armstrong K9 Registry</title>
        <meta name="robots" content="noindex" />
        <style>{`
          html, body, #root { background: #050505; margin: 0; overflow: hidden; }
        `}</style>
      </Helmet>
      <ReelStage
        accentColor="#C9A227"
        brandLine={"The trust-and-safety standard\nfor purebred and exotic dogs."}
      >
        <BeatHook />
        <BeatMarketplace />
        <BeatVerifiedZoom />
        <BeatPedigreeFan />
        <BeatBuyerPass />
      </ReelStage>
    </>
  );
}

/* -------------------------------------------------------------- */
/* Beat 1 — Hook                                                   */
/* -------------------------------------------------------------- */
function BeatHook() {
  return (
    <Beat start={0.0} end={2.6}>
      {(p) => {
        const ep = easeOutCubic(p);
        const sealOpen = beatProgress(p, 0.05, 0.55);
        const sealScale = 0.4 + easeOutCubic(sealOpen) * 0.6;
        const headlineMask = beatProgress(p, 0.35, 1.0);
        const kickerOpacity = Math.min(1, beatProgress(p, 0.1, 0.4));
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
            <div
              className="relative"
              style={{
                transform: `scale(${sealScale})`,
                filter: `drop-shadow(0 8px 40px rgba(201,162,39,${0.2 + sealOpen * 0.45}))`,
              }}
            >
              <WaxSeal size={140} title="Armstrong K9 Registry" />
            </div>
            <p
              className="mt-12 uppercase font-bold text-[20px] tracking-[0.45em] text-gold-light"
              style={{ opacity: kickerOpacity }}
            >
              For Buyers
            </p>
            <h1
              className="mt-10 font-spectral font-light text-white"
              style={{
                fontFamily: '"Spectral", Georgia, serif',
                fontSize: 110,
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                maxWidth: 900,
                clipPath: `inset(0 ${(1 - easeInOutCubic(headlineMask)) * 100}% 0 0)`,
                transform: `translateY(${(1 - ep) * 12}px)`,
              }}
            >
              Your next<br />family member.
            </h1>
            <div
              className="mt-12 h-px"
              style={{
                width: easeOutCubic(headlineMask) * 360,
                background:
                  "linear-gradient(90deg, transparent, rgba(201,162,39,0.95), transparent)",
              }}
            />
          </div>
        );
      }}
    </Beat>
  );
}

/* -------------------------------------------------------------- */
/* Beat 2 — Marketplace parallax                                   */
/* -------------------------------------------------------------- */

// Mock listings — purely on-platform, no external images. Color tints
// + breed silhouettes emulate the marketplace "photo placeholder" state
// every breeder sees on day one before they upload.
const LISTINGS = [
  { name: "Otto", breed: "Cane Corso", price: "$5,200", tone: "from-[#1f1c17] to-[#3a2e1d]", verified: true, sex: "♂" },
  { name: "Sable", breed: "Belgian Malinois", price: "$3,800", tone: "from-[#22251c] to-[#3a3f25]", verified: true, sex: "♀" },
  { name: "Brutus", breed: "Presa Canario", price: "$6,500", tone: "from-[#241a1a] to-[#3f2828]", verified: true, sex: "♂" },
  { name: "Aria", breed: "Doberman Pinscher", price: "$4,200", tone: "from-[#1d1d1d] to-[#2e2e2e]", verified: true, sex: "♀" },
  { name: "Roman", breed: "Dogo Argentino", price: "$5,000", tone: "from-[#23201d] to-[#42382e]", verified: false, sex: "♂" },
  { name: "Vela", breed: "Tibetan Mastiff", price: "$8,900", tone: "from-[#1a1d22] to-[#2f3742]", verified: true, sex: "♀" },
];

function BeatMarketplace() {
  return (
    <Beat start={2.6} end={5.6}>
      {(p) => {
        const ep = easeInOutCubic(p);
        // Grid scrolls up roughly 40% of its height across the beat,
        // creating the parallax. Header text rides on top, fixed.
        const scrollY = -ep * 1100;
        const captionAlpha = Math.min(1, beatProgress(p, 0.1, 0.45));
        const captionOut = 1 - beatProgress(p, 0.85, 1.0);
        return (
          <div className="absolute inset-0 overflow-hidden">
            {/* Card grid */}
            <div
              className="absolute left-0 right-0"
              style={{
                top: 280,
                transform: `translate3d(0, ${scrollY}px, 0)`,
              }}
            >
              <div className="grid grid-cols-2 gap-8 px-16">
                {[...LISTINGS, ...LISTINGS].map((l, i) => (
                  <ListingCard key={i} l={l} />
                ))}
              </div>
            </div>
            {/* Top fade so cards emerge cleanly */}
            <div
              aria-hidden="true"
              className="absolute top-0 left-0 right-0 h-72 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, #050505 30%, rgba(5,5,5,0.6) 70%, transparent 100%)",
              }}
            />
            <div
              aria-hidden="true"
              className="absolute bottom-0 left-0 right-0 h-72 pointer-events-none"
              style={{
                background:
                  "linear-gradient(0deg, #050505 30%, rgba(5,5,5,0.7) 70%, transparent 100%)",
              }}
            />
            {/* Title overlay */}
            <div
              className="absolute top-32 left-0 right-0 text-center px-16"
              style={{
                opacity: captionAlpha * captionOut,
                transform: `translateY(${(1 - captionAlpha) * -10}px)`,
              }}
            >
              <p className="uppercase tracking-[0.35em] text-[16px] text-crimson font-bold">
                The Marketplace
              </p>
              <h2
                className="mt-4 font-spectral font-light"
                style={{
                  fontFamily: '"Spectral", Georgia, serif',
                  fontSize: 78,
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                }}
              >
                65 breeds.<br />Verified kennels only.
              </h2>
            </div>
          </div>
        );
      }}
    </Beat>
  );
}

function ListingCard({ l }) {
  return (
    <div
      className={`relative h-[360px] rounded-2xl overflow-hidden bg-gradient-to-br ${l.tone} ring-1 ring-white/10 shadow-[0_18px_50px_rgba(0,0,0,0.6)]`}
    >
      {/* Photo placeholder area — large breed silhouette */}
      <div className="absolute inset-0 flex items-end justify-center pb-24 opacity-40">
        <Dog size={220} weight="duotone" className="text-gold/45" />
      </div>
      {/* Verified seal */}
      {l.verified && (
        <div className="absolute top-4 right-4">
          <WaxSeal size={36} title="Verified" />
        </div>
      )}
      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/85 to-transparent">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p
              className="font-spectral text-[28px] tracking-tight truncate"
              style={{ fontFamily: '"Spectral", Georgia, serif', lineHeight: 1 }}
            >
              {l.name} <span className="text-gold-light text-[20px]">{l.sex}</span>
            </p>
            <p className="text-[15px] text-white/70 mt-1 truncate">{l.breed}</p>
          </div>
          <span
            className="px-3 py-1.5 rounded-full bg-gold-gradient text-ink font-semibold text-[15px] whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, #E6D070 0%, #C9A227 100%)" }}
          >
            {l.price}
          </span>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
/* Beat 3 — Verified zoom                                          */
/* -------------------------------------------------------------- */
function BeatVerifiedZoom() {
  return (
    <Beat start={5.6} end={8.4}>
      {(p) => {
        const ep = easeOutCubic(p);
        const sealPulse = 1 + Math.sin(p * Math.PI * 3) * 0.06;
        const captionAlpha = Math.min(1, beatProgress(p, 0.25, 0.6));
        const captionOut = 1 - beatProgress(p, 0.85, 1.0);
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            {/* The hero card scales up 0.85 → 1.0, then zooms slightly past 1.0 */}
            <div
              style={{
                width: 720,
                transform: `scale(${0.78 + ep * 0.32})`,
              }}
            >
              <div className="relative h-[820px] rounded-3xl overflow-hidden bg-gradient-to-br from-[#1f1c17] to-[#3a2e1d] ring-1 ring-gold/45 shadow-[0_30px_80px_rgba(201,162,39,0.18),0_30px_80px_rgba(0,0,0,0.7)]">
                <div className="absolute inset-0 flex items-center justify-center opacity-45">
                  <Dog size={520} weight="duotone" className="text-gold/55" />
                </div>
                {/* Pulsing seal — anchor of the verified moment */}
                <div
                  className="absolute top-8 right-8"
                  style={{
                    transform: `scale(${sealPulse})`,
                    filter: "drop-shadow(0 6px 30px rgba(201,162,39,0.55))",
                  }}
                >
                  <WaxSeal size={88} title="Verified Kennel" />
                </div>
                {/* Bottom info */}
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/90 to-transparent">
                  <p
                    className="font-spectral text-[58px] tracking-tight"
                    style={{ fontFamily: '"Spectral", Georgia, serif', lineHeight: 1 }}
                  >
                    Otto <span className="text-gold-light text-[36px]">♂</span>
                  </p>
                  <p className="text-[24px] text-white/75 mt-2">Cane Corso · 14 weeks</p>
                  <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <Chip icon={ShieldCheck} label="Identity verified" />
                    <Chip icon={CheckCircle} label="Microchip on file" />
                    <Chip icon={CheckCircle} label="Health tested" />
                  </div>
                </div>
              </div>
            </div>
            {/* Caption */}
            <div
              className="absolute bottom-32 left-0 right-0 text-center px-16"
              style={{
                opacity: captionAlpha * captionOut,
                transform: `translateY(${(1 - captionAlpha) * 12}px)`,
              }}
            >
              <p className="uppercase tracking-[0.35em] text-[16px] text-crimson font-bold">
                Trust, earned
              </p>
              <p
                className="mt-3 font-spectral font-light text-white"
                style={{
                  fontFamily: '"Spectral", Georgia, serif',
                  fontSize: 56,
                  lineHeight: 1.05,
                }}
              >
                Every kennel, identity-verified.
              </p>
            </div>
          </div>
        );
      }}
    </Beat>
  );
}

function Chip({ icon: IconCmp, label }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm ring-1 ring-white/15 text-[15px] text-white/90">
      <IconCmp size={16} weight="bold" className="text-gold-light" />
      {label}
    </span>
  );
}

/* -------------------------------------------------------------- */
/* Beat 4 — Pedigree fan                                           */
/* -------------------------------------------------------------- */
function BeatPedigreeFan() {
  // Fan opens like an heraldic crest. Each generation is a row of
  // nodes (1, 2, 4, 8, 16, 32) but we cap visual nodes to 1+2+4+8
  // for legibility — the labels still announce 6 generations.
  return (
    <Beat start={8.4} end={11.2}>
      {(p) => {
        const ep = easeOutCubic(p);
        const captionAlpha = Math.min(1, beatProgress(p, 0.2, 0.55));
        const captionOut = 1 - beatProgress(p, 0.85, 1.0);
        const rows = [1, 2, 4, 8, 16];
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div
              className="relative flex flex-col items-center gap-6"
              style={{ width: 900 }}
            >
              {rows.map((count, rowIdx) => {
                const rowDelay = rowIdx * 0.08;
                const rowP = Math.max(0, ep - rowDelay) / Math.max(0.01, 1 - rowDelay);
                const rowOpacity = Math.min(1, rowP * 1.4);
                return (
                  <div
                    key={rowIdx}
                    className="flex items-center justify-center gap-3"
                    style={{
                      opacity: rowOpacity,
                      transform: `scale(${0.7 + rowP * 0.3})`,
                    }}
                  >
                    {Array.from({ length: count }).map((_, i) => (
                      <PedigreeNode key={i} small={count > 4} />
                    ))}
                  </div>
                );
              })}
            </div>
            {/* Caption */}
            <div
              className="absolute bottom-40 left-0 right-0 text-center px-16"
              style={{
                opacity: captionAlpha * captionOut,
              }}
            >
              <p className="uppercase tracking-[0.35em] text-[16px] text-crimson font-bold">
                Six generations deep
              </p>
              <p
                className="mt-3 font-spectral font-light text-white"
                style={{
                  fontFamily: '"Spectral", Georgia, serif',
                  fontSize: 56,
                  lineHeight: 1.05,
                }}
              >
                Audit any pedigree<br />before you commit.
              </p>
            </div>
          </div>
        );
      }}
    </Beat>
  );
}

function PedigreeNode({ small }) {
  const size = small ? 36 : 64;
  return (
    <div
      className="rounded-md bg-cream ring-1 ring-gold/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_2px_8px_rgba(201,162,39,0.18)] flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <Dog size={small ? 18 : 30} weight="duotone" className="text-ink/80" />
    </div>
  );
}

/* -------------------------------------------------------------- */
/* Beat 5 — Buyer Pass / Escrow                                    */
/* -------------------------------------------------------------- */
function BeatBuyerPass() {
  return (
    <Beat start={11.2} end={13.4}>
      {(p) => {
        const ep = easeOutCubic(p);
        const slide = (1 - ep) * 80;
        const cardOpacity = Math.min(1, p * 2.2);
        return (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-16">
            <p
              className="uppercase tracking-[0.35em] text-[16px] text-crimson font-bold"
              style={{ opacity: cardOpacity }}
            >
              Buyer protection
            </p>
            <h2
              className="mt-4 font-spectral font-light text-white text-center"
              style={{
                fontFamily: '"Spectral", Georgia, serif',
                fontSize: 64,
                lineHeight: 1.05,
                opacity: cardOpacity,
                transform: `translateY(${slide * 0.3}px)`,
              }}
            >
              Stripe escrow.<br />Refundable deposit.
            </h2>
            <div
              className="mt-14 w-[760px] rounded-2xl bg-cream text-ink ring-1 ring-gold/55 shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden"
              style={{
                opacity: cardOpacity,
                transform: `translateY(${slide}px)`,
              }}
            >
              <div className="px-9 py-7 border-b border-[#E2DAC2] flex items-center justify-between">
                <span
                  className="font-spectral text-[28px] tracking-tight"
                  style={{ fontFamily: '"Spectral", Georgia, serif' }}
                >
                  Buyer Pass — Otto
                </span>
                <span className="px-3 py-1.5 rounded-full bg-gold-gradient text-ink font-semibold text-[16px]"
                  style={{ background: "linear-gradient(135deg, #E6D070 0%, #C9A227 100%)" }}>
                  $5,200 escrow
                </span>
              </div>
              <div className="px-9 py-6 grid grid-cols-2 gap-y-4 gap-x-6 text-[19px]">
                <Row icon={ShieldCheck} label="Funds held by Stripe" />
                <Row icon={CheckCircle} label="Health screening on file" />
                <Row icon={Storefront} label="Verified kennel" />
                <Row icon={HeartStraight} label="Refundable until pickup" />
              </div>
              <div className="px-9 py-5 bg-[#FAF6EE] flex items-center justify-between text-[16px] text-ink/70">
                <span>Released only when the dog is signed over.</span>
                <span className="font-mono">~2 day payout</span>
              </div>
            </div>
          </div>
        );
      }}
    </Beat>
  );
}

function Row({ icon: IconCmp, label }) {
  return (
    <span className="inline-flex items-center gap-3">
      <IconCmp size={22} weight="bold" className="text-gold" />
      <span>{label}</span>
    </span>
  );
}
